'use client'

import { Button } from '@/components'
import { appSelectors, useAppStore } from '@/store'
import { useTranslations } from 'next-intl'
import { Else, If, Then } from 'react-if'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  LinkButton,
} from '@/components/ui'
import { ClientRoutes } from '@/config'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'
import { useLocalizedRoutes } from '@/lib/hooks'
import { AvatarIcon } from '@radix-ui/react-icons'

export const AuthToolbar: React.FC = () => {
  const t = useTranslations('auth')
  const { setSignupVisible } = useAppStore(appSelectors.actions)
  const { logout } = useAppStore(appSelectors.asyncActions)
  const { isSignUpVisible, loading } = useAppStore(appSelectors.status)
  const { user, isLoggedIn } = useAppStore(appSelectors.auth)
  const router = useRouter()
  const { get } = useLocalizedRoutes()

  const onLogout = useCallback(
    () =>
      logout(() => {
        router.replace(get(ClientRoutes.Home))
      }),
    [logout, router, get],
  )

  const btnClass = 'text-white border-white/40 rounded-full'

  return (
    <If condition={!loading && isLoggedIn}>
      <Then>
        <div className="flex gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild className="text-white/60">
              <Button variant="ghost" className="rounded-full">
                <AvatarIcon className="lg:hidden" />
                <span className="hidden lg:block">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-56 rounded-lg bg-primary/75 border-white/10 text-white/60 z-99"
              align="end"
            >
              <DropdownMenuLabel>{t('menu.group.account')}</DropdownMenuLabel>
              <DropdownMenuGroup>
                <DropdownMenuItem className="text-left hover:bg-glow/10">
                  <LinkButton
                    variant="destructive"
                    className="flex-1 block text-left"
                    href={get(ClientRoutes.profile)}
                  >
                    {t('menu.profile')}
                  </LinkButton>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuLabel>{t('menu.group.crawling')}</DropdownMenuLabel>
              <DropdownMenuGroup>
                <DropdownMenuItem className="text-left hover:bg-glow/10">
                  <LinkButton
                    variant="destructive"
                    className="flex-1 block text-left"
                    href={get(ClientRoutes.history)}
                  >
                    {t('menu.history')}
                  </LinkButton>
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuLabel>{t('menu.group.common')}</DropdownMenuLabel>
              <DropdownMenuGroup>
                <DropdownMenuItem className="text-left hover:bg-glow/10">
                  <LinkButton
                    variant="destructive"
                    className="flex-1 block text-left"
                    href={get(ClientRoutes.contact)}
                  >
                    {t('menu.contact')}
                  </LinkButton>
                </DropdownMenuItem>
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button variant="outline" className={btnClass} onClick={onLogout}>
            {t('actions.logout')}
          </Button>
        </div>
      </Then>
      <Else>
        <If condition={loading}>
          <Then>
            <div className="w-1/6 animate-pulse min-w-[100px] rounded-full h-8 mt-1 bg-glow/10" />
          </Then>
          <Else>
            <Button
              variant="outline"
              className={btnClass}
              onClick={() => setSignupVisible(!isSignUpVisible)}
            >
              {t('actions.signInOrUp')}
            </Button>
          </Else>
        </If>
      </Else>
    </If>
  )
}
